//import packages from node modules
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import renderHTML from 'react-render-html'
import { change, Field, formValueSelector, hasSubmitFailed } from 'redux-form'

//import utilities functions
import { emailFieldValidation, requiredFieldValidation } from "../../form/validation"
//import {cancelSubmitAUSCheck,cancelSubmitEPPCheck} from "../../../../utilities/api/product"
import { cancelSubmitAUSCheck } from "../../../../utilities/api/product"
//import shared components
import TextField, { renderDescription, renderLabel } from "../../form/textfield"
import { getElementRefValue, isAUDomain } from "../../../../utilities/function/common"
import SolidButton from "../../button/solidbutton"
import Dropdown from "../../form/dropdown"

//import internals
import { ELIGIBILITY_NUMBER, ELIGIBILITY_TYPE, EPP_CODE, TRANSFER_RENEW } from "./index"

import {
    getDataFromSuccessResponse,
    getErrorFromFailResponse,
    isResponseCancelled
} from "../../../../utilities/function/apiResponse"

import {
    getAppConfigValue,
    getAttributes,
    getDomainNameCID,
    getDomainNameFromProductConfig
} from "../../../../utilities/function/configuration"

import { SELECT_DOMAIN_CONFIG_FORM } from "./SelectDomainConfigForm"
import Loading from "../../loading";
import { getCycle, getYearFromPriceName } from "../../../../utilities/function/billingCycle";
import { updateConfig } from "../../../../app/action";


const AsyncValidationField = (props) => {
    const formSelector = formValueSelector(SELECT_DOMAIN_CONFIG_FORM)
    const dispatch = useDispatch()
    const { requireButton, field, errorMessages, getInputName, asyncValidation, item } = props
    const { name, label, placeholder, description } = field
    const FIELD_ELIGIBILITY_TYPE = getInputName(ELIGIBILITY_TYPE)
    const {
        eligibilityType,
        currentFieldValue,
        submitFailed,
        transferRenewValue,
        configurations
    } = useSelector(state => ({
        eligibilityType: formSelector(state, FIELD_ELIGIBILITY_TYPE),
        transferRenewValue: formSelector(state, getInputName(TRANSFER_RENEW)),
        currentFieldValue: formSelector(state, name),
        submitFailed: hasSubmitFailed(SELECT_DOMAIN_CONFIG_FORM)(state),
        configurations: state.app.configurations
    }))

    const validatorFieldElement = useRef(null)
    const prevIsValid = useRef(null)
    const [asynValidateRequest, setAsynValidateRequest] = useState({})
    const [focus, setFocus] = useState(null)
    const [useAnywayFlag, setUseAnywayFlag] = useState(false)
    const [isTouched, setIsTouched] = useState(false)

    const cid = getDomainNameCID(item)

    const { asyncFields: asyncFieldsCache, asynValidateRequest: asynValidateRequestCache } = item || {}

    const isValid = useCallback(() => {
        if (!getElementRefValue(validatorFieldElement) && (isTouched || submitFailed)) {
            return 'blank'
        }
        const handleSuccess = () => {
            const handleEligibilityNumber = () => {
                if (asynValidateRequest && asynValidateRequest.data) {
                    const { status, entityStatus } = asynValidateRequest.data
                    if (status === "OK" && (entityStatus === "Active" || entityStatus === "Registered" || entityStatus === 'Registered/protected')) {
                        return 'eligibility success'
                    }
                }
                return false
            }

            const handleEPPCode = () => {
                if (asynValidateRequest && asynValidateRequest.data) {
                    const { is_transferable } = asynValidateRequest.data
                    if (is_transferable) {
                        return 'epp success'
                    }
                }
            }

            switch (name) {
                case getInputName(ELIGIBILITY_NUMBER):
                    return handleEligibilityNumber()
                case getInputName(EPP_CODE):
                    return handleEPPCode()
                default:
                    return false
            }
        }

        switch (asynValidateRequest.status) {
            case 'error':
                return false
            case 'success':
                return handleSuccess()
            default:
                return 'initial'
        }
    }, [asynValidateRequest, getInputName, isTouched, name, submitFailed])
    const onSuccessCallback = useCallback(() => {
        if (prevIsValid.current !== isValid()) {
            if (isValid() === "epp success" || isValid() === "eligibility success") {
                dispatch(change(SELECT_DOMAIN_CONFIG_FORM, name, getElementRefValue(validatorFieldElement)))
            }
            prevIsValid.current = isValid()
        }
    }, [name, dispatch, validatorFieldElement, isValid, prevIsValid])
    const onAsyncValidate = useCallback(() => {
        const handleSuccessResponse = (successResponse) => {
            const data = getDataFromSuccessResponse(successResponse)
            updateConfig(configurations, cid, {
                asyncFields: {
                    ...asyncFieldsCache,
                    eligibilityType,
                    currentFieldValue: validatorFieldElement.current && validatorFieldElement.current.value
                },
                asynValidateRequest: {
                    status: "success",
                    data
                }
            }, dispatch)
            setAsynValidateRequest({
                ...asyncValidation,
                status: 'success',
                data
            })
        }
        const handleErrorResponse = (errorResponse) => {
            const error = getErrorFromFailResponse(errorResponse)
            if (!isResponseCancelled(error)) {
                setAsynValidateRequest({
                    ...asynValidateRequest,
                    status: 'error',
                    error
                })
            }
        }
        if (getElementRefValue(validatorFieldElement)) {
            setAsynValidateRequest({
                ...asynValidateRequest,
                status: 'loading'
            })
            const validateEligiblityNumber = () => {
                if (!requireButton && getElementRefValue(validatorFieldElement)) {
                    asyncValidation(getElementRefValue(validatorFieldElement), eligibilityType).then(successResponse => {
                        handleSuccessResponse(successResponse)
                    }, errorResponse => {
                        handleErrorResponse(errorResponse)
                    })
                }
            }
            const validateEPPCode = () => {
                const domain = getDomainNameFromProductConfig(item)
                asyncValidation(domain, getElementRefValue(validatorFieldElement)).then(successResponse => {
                    handleSuccessResponse(successResponse)
                }, errorResponse => {
                    handleErrorResponse(errorResponse)
                })
            }
            switch (name) {
                case getInputName(ELIGIBILITY_NUMBER):
                    validateEligiblityNumber()
                    break
                case getInputName(EPP_CODE):
                    validateEPPCode()
                    break
                default:
                    break
            }
        }
    }, [eligibilityType, validatorFieldElement, getInputName, asynValidateRequest, item, name, requireButton, asyncValidation, asyncFieldsCache, cid, configurations, dispatch])
    const resetValue = useCallback(() => {
        cancelPrevRequest()
        dispatch(change(SELECT_DOMAIN_CONFIG_FORM, name, ''))
        setAsynValidateRequest({})
        if (useAnywayFlag) {
            setUseAnywayFlag(false)
        }
    }, [dispatch, name, useAnywayFlag])
    const prevEligibilityType = useRef(null)
    const onPrevEligibilityTypeCallback = useCallback(() => {
        if (eligibilityType && prevEligibilityType.current !== eligibilityType) {
            resetValue()
            onAsyncValidate()
            prevEligibilityType.current = eligibilityType
        }
    }, [prevEligibilityType, eligibilityType, onAsyncValidate, resetValue])
    const prevTransferRenewValue = useRef(null);
    const transferRenewTimeout = useRef(null);

    const onChangeTransferRenewField = useCallback(() => {
        if (prevTransferRenewValue.current !== transferRenewValue) {
            if (transferRenewValue) {
                if (transferRenewTimeout.current) {
                    window.clearTimeout(transferRenewTimeout.current)
                }
                transferRenewTimeout.current = window.setTimeout(() => {
                    dispatch(change(SELECT_DOMAIN_CONFIG_FORM, getInputName("billing_cycle_id"), transferRenewValue))

                }, 500)
            }
            prevTransferRenewValue.current = transferRenewValue
        }
    }, [transferRenewValue, prevTransferRenewValue, dispatch, getInputName])

    const initValueFlag = useRef(false)
    const initFlag = useRef(false)
    const onEnterCallbackReference = useRef((e) => {
        if (e.key === "Enter") {
            setIsTouched(true)
            resetValue()
            onAsyncValidate()
        }
    })

    const cancelPrevRequest = () => {
        /*if(cancelSubmitEPPCheck){
            cancelSubmitEPPCheck()
        }*/
        if (cancelSubmitAUSCheck) {
            cancelSubmitAUSCheck()
        }
    }

    const isWarning = () => {
        return !requireButton && !isValid() && isValid() !== undefined
    }

    const getColor = (type = 'border') => {
        if (isValid()) {
            if (isValid() === "blank" && (isTouched || submitFailed)) {
                return `status-${type}-2-100`
            }
            if (isValid() === "epp success") {
                return `status-${type}-1-100`
            }
            if (focus) {
                return `primary-${type}-1-100`
            }
            return `lines-${type}-1-100`
        }
        if (requireButton) {
            return `status-${type}-2-100`
        }
        return `status-${type}-3-100`
    }

    const getEPPCodeMessages = () => {
        const companyName = getAppConfigValue('company_name')
        const companyEmail = getAppConfigValue('company_email')
        const CCPName = getAppConfigValue('ccp_name')
        const moveDomainNameLink = getAppConfigValue('move_domain_link')
        const EPP_INVALID = 'epp_invalid'
        const ALREADY_IN_ACCOUNT = 'already_in_account'
        const TRANSFER_LOCK = 'transfer_lock'
        const NOT_TRANSFERRABLE_WITHIN_60_DAYS = 'not_transferrable_within_60_days'
        const fallbackMessage = {
            validation: 'Something has gone wrong',
            heading: 'We\'re sorry!',
            description: `If you're unable to proceed please contact our Sales team at <a class="primary-font-1-100" href="mailto:${companyEmail}">${companyEmail}</a> or start a new live chat for assistance.`,
            forceButton: false
        }
        if (asynValidateRequest.data && asynValidateRequest.status === "success") {
            if (name === getInputName(EPP_CODE)) {
                const { reason, is_transferable, registrant_email } = asynValidateRequest.data
                if (is_transferable) {
                    const text = registrant_email && !emailFieldValidation(registrant_email) ? ' the email address listed below' : ' the registrant contact email address'
                    return {
                        validation: 'EPP code has been validated as correct',
                        heading: 'Access to the registrant contact email address',
                        description: `Before continuing, please ensure you have access to ${text}. Once the domain transfer has been initiated this email address will be sent a link to approve the transfer.`,
                        box: {
                            description: 'Approval link will be sent to ',
                            heading: !emailFieldValidation(registrant_email) ? registrant_email : null
                        }
                    }
                }
                switch (reason) {
                    case EPP_INVALID:
                        return {
                            validation: 'EPP code does not appear to be valid',
                            heading: 'Making sure your EPP code is correct',
                            description: 'Be sure to copy and paste your EPP code to avoid adding or missing any characters, otherwise you can proceed with the transfer by clicking the button below and correcting the EPP code later.',
                            forceButton: true
                        }
                    case ALREADY_IN_ACCOUNT:
                        return {
                            validation: `Domain name already with ${companyName}`,
                            heading: `Need to move this domain into another ${CCPName} account?`,
                            description: `If you need to move this domain name into another VIPControl account please use our Move Service tool to do so. <a class="primary-font-1-100" target="_blank" href="${moveDomainNameLink ? moveDomainNameLink : '#'}">Read more</a>`,
                            forceButton: false
                        }
                    case TRANSFER_LOCK:
                        return {
                            validation: 'Domain transfer prohibited by the current Registrar',
                            heading: 'Your domain name may be locked',
                            description: 'Please contract your current domain provider and ask them to unlock the domain for transfer, otherwise you can proceed with the transfer by clicking the button below and unlocking the domain later.',
                            forceButton: true
                        }
                    case NOT_TRANSFERRABLE_WITHIN_60_DAYS:
                        return {
                            validation: 'Domain transfer prohibited for the first 60 days after registration',
                            heading: 'Your domain name may be locked',
                            description: 'Please contract your current domain provider and ask them to unlock the domain for transfer, or wait for the 60-day lock to end.',
                            forceButton: false
                        }
                    default:
                        return fallbackMessage
                }
            }
        }
        return fallbackMessage
    }

    const onChangeCallback = () => {
        resetValue()
        if (!requireButton) {
            onAsyncValidate()
        }
    }

    const onClickCallback = () => {
        setIsTouched(true)
        resetValue()
        onAsyncValidate()
    }

    const onFocusCallback = () => {
        setFocus(true)
        if (requireButton) {
            document.addEventListener("keyup", onEnterCallbackReference.current)
        }
    }

    const onBlurCallback = () => {
        setFocus(false)
        setIsTouched(true)
        if (requireButton) {
            document.removeEventListener("keyup", onEnterCallbackReference.current)
        }
    }

    const initValueCallback = useCallback(() => {
        const {
            currentFieldValue: currentFieldValueCache,
            eligibilityType: eligibilityTypeCache
        } = asyncFieldsCache || {}
        if (!initValueFlag.current && (currentFieldValue || currentFieldValueCache)) {
            validatorFieldElement.current.value = currentFieldValue || currentFieldValueCache
            if (eligibilityTypeCache) {
                dispatch(change(SELECT_DOMAIN_CONFIG_FORM, getInputName(ELIGIBILITY_TYPE), eligibilityTypeCache));
            }
            if (asynValidateRequestCache) {
                setAsynValidateRequest(asynValidateRequestCache)
            }
            initValueFlag.current = true
        }
    }, [validatorFieldElement, currentFieldValue, asyncFieldsCache, asynValidateRequestCache, getInputName, dispatch])

    const onInitCallback = useCallback(() => {
        if (!initFlag.current && validatorFieldElement.current && validatorFieldElement.current.value && !requireButton) {
            onAsyncValidate()
            initFlag.current = true
        }
    }, [initFlag, requireButton, validatorFieldElement, onAsyncValidate])

    useEffect(() => {
        onPrevEligibilityTypeCallback()
        initValueCallback()
        onInitCallback()
        onSuccessCallback()
        onChangeTransferRenewField()
    }, [onPrevEligibilityTypeCallback, initValueCallback, onInitCallback, onSuccessCallback, onChangeTransferRenewField])

    const renderButton = () => {
        const { status } = asynValidateRequest || {}
        if (requireButton && status !== "loading" && !useAnywayFlag && isValid() !== "epp success") {
            return (
                <SolidButton onClick={onClickCallback} className={'of-domain-config-validator-field__button primary-background-2-100 text-font-1-100'}>
                    Validate
                </SolidButton>
            )
        }
    }

    const getMessages = () => {
        let messages = {}
        if (errorMessages && !isValid()) {
            messages = errorMessages
        }
        //overwrite error messages if this epp code field
        if (name === getInputName(EPP_CODE)) {
            messages = getEPPCodeMessages()
        }

        return {
            ...messages
        }
    }

    const renderMessages = () => {
        const renderUseAnywayButton = () => {
            if (!isValid()) {
                const { forceButton } = getMessages()
                const val = getElementRefValue(validatorFieldElement)
                const useAnywayCallback = (e) => {
                    e.preventDefault()
                    if (val) {
                        dispatch(change(SELECT_DOMAIN_CONFIG_FORM, name, val))
                        setUseAnywayFlag(true)
                    }
                }
                const getUseAnywayText = () => {
                    return useAnywayFlag ? 'Skipped' : 'Skip For Now'
                }

                if (val && forceButton) {
                    return (
                        <SolidButton className={'of-domain-config-validator-use-anyway-button'}
                                     isDisabled={useAnywayFlag} type={"onClick"} onClick={useAnywayCallback}>
                            {getUseAnywayText()}
                        </SolidButton>
                    )
                }
            }
            return (<></>)
        }
        const renderContent = () => {
            const { heading, description } = getMessages()
            return (
                <>
                    <div className={'of-domain-config-validator-messages__heading text-font-1-100'}>
                        {heading}
                    </div>
                    <div className={'of-domain-config-validator-messages__description text-font-3-100'}>
                        {description ? renderHTML(description) : ''}
                    </div>
                </>
            )
        }
        const renderBox = () => {
            const { box } = getMessages()

            if (box) {
                const { heading, description } = box

                if (heading) {
                    return (
                        <div className={'of-domain-config-validator-messages-box shades-secondary-background-1-100'}>
                            <div
                                className={'of-domain-config-validator-messages-box__description text-font-1-100'}>{description ? renderHTML(description) : ''}</div>
                            <div
                                className={'of-domain-config-validator-messages-box__heading text-font-1-100'}>{heading}</div>
                        </div>
                    )
                }
            }
            return (<></>)
        }
        if (asynValidateRequest.status && asynValidateRequest.status !== "loading" && getMessages()) {
            const { heading } = getMessages()
            if (heading) {
                return (
                    <div className={'of-domain-config-validator-messages lines-border-1-100 clearfix'}>
                        {renderContent()}
                        {renderUseAnywayButton()}
                        {renderBox()}
                    </div>
                )
            }
        }
        return (<></>)
    }

    const renderLoading = () => {
        if (asynValidateRequest.status === "loading") {
            return (
                <div className={'of-domain-config-validator-field__loading'}>
                    <Loading className={'of-domain-config-validator-field__loading-img'}/>
                </div>
            )
        }
        return (<></>)
    }

    const renderValidatorField = () => {
        const renderFieldValidationMessage = () => {
            if (asynValidateRequest.status !== "loading") {
                if (isValid() === "blank") {
                    return (
                        <div
                            className={`of-domain-config-validator-field__validation-message of-textfield__error ${getColor('font')}`}>
                            {label} Required
                        </div>)
                }
                if (asynValidateRequest.status && getElementRefValue(validatorFieldElement)) {
                    const { validation } = getMessages()
                    if (validation) {
                        //only render success messages
                        if (isValid() === "epp success") {
                            return (
                                <div
                                    className={`of-domain-config-validator-field__validation-message of-textfield__error ${getColor('font')}`}>
                                    <i className={'icon-of-tick-solid'}/>{validation}
                                </div>
                            )
                        }
                        if (isWarning() && asynValidateRequest.status !== "loading") {
                            return (
                                <div
                                    className={`of-domain-config-validator-field__validation-message of-textfield__error ${getColor('font')}`}>
                                    <i className={'of-domain-config-validator-field__warning-icon icon-of-warning'}/>
                                    {validation}
                                </div>
                            )
                        }
                        return (
                            <div
                                className={`of-domain-config-validator-field__validation-message of-textfield__error ${getColor('font')}`}>
                                <i className={'icon-of-x'}/>
                                {validation}
                            </div>
                        )
                    }
                }
            }
            return (<></>)
        }
        return (
            <div className={'of-domain-config-validator-field'}>
                <div className={'of-textfield'}>
                    {renderLabel(label, getElementRefValue(validatorFieldElement))}
                    {renderDescription(description)}
                    {renderButton()}
                    <input placeholder={placeholder}
                           type={"text"}
                           className={`${isWarning() ? ' of-domain-config-validator-field__input--warning ' : ''}of-domain-config-validator-field__input of-textfield__input ${getColor()}`}
                           onChange={onChangeCallback}
                           ref={validatorFieldElement}
                           onFocus={onFocusCallback}
                           onBlur={onBlurCallback}
                    />
                    {renderLoading()}
                </div>
                {renderFieldValidationMessage()}
            </div>
        )
    }

    const renderLegalName = () => {
        if (
            ELIGIBILITY_TYPE &&
            asynValidateRequest.data &&
            asynValidateRequest.status === "success" &&
            (asynValidateRequest.data.entityStatus === 'Active' || asynValidateRequest.data.entityStatus === 'Registered' || asynValidateRequest.data.entityStatus === 'Registered/protected')
        ) {
            const { legalName, entityName } = asynValidateRequest.data
            const businessName = legalName || entityName

            if (businessName) {
                return (
                    <div className={`of-domain-config__field-wrapper`}>
                        <div className={'of-domain-config-validator-field'}>
                            <div className={'of-textfield'}>
                                {renderLabel('Legal Name', getElementRefValue(validatorFieldElement))}
                                <input
                                    className={`of-textfield__input lines-border-1-100`}
                                    readOnly={true}
                                    disabled={true}
                                    type={"text"}
                                    value={businessName}
                                />
                            </div>
                        </div>
                    </div>
                )
            }
        }
        return (<></>)
    }

    const renderHiddenField = () => {
        return (
            <Field
                type={"hidden"}
                className={"of-domain-config-validator-hidden-field"}
                component={TextField}
                name={name}
                validate={[requiredFieldValidation]}
            />
        )
    }

    const renderTransferRenewalField = () => {
        const { data } = asynValidateRequest || {}
        const { max_years_can_renew, is_transferable, within_renewal_period } = data || {}
        const { price, name: tld } = (item && getAttributes(item.selectedProduct)) || {}
        const { domainSearchResult } = item || {}
        const { price: checkDomainPrice } = domainSearchResult || {}
        const showDropdown = max_years_can_renew && is_transferable && within_renewal_period && !isAUDomain(tld)
        const { billing_cycle_id } = item || {}

        const selectedPrice = checkDomainPrice || price

        const transferRenewPrice = selectedPrice && selectedPrice.length && selectedPrice.filter(i => {
            return (max_years_can_renew && i.type === "transfer" && (getYearFromPriceName(i.name) <= max_years_can_renew));
        }).map(i => ({
            label: <label className={"text-font-3-100"}><span>{i.name}</span> <strong className={"text-font-1-100"}> -
                ${i.price}{getCycle(i.name)}</strong></label>,
            value: i.billing_cycle_id
        }))

        if (showDropdown && transferRenewPrice && transferRenewPrice.length) {
            return (
                <Field
                    initialValue={billing_cycle_id}
                    label={"Transfer & Renewal Period"}
                    className={"of-domain-config__transfer-renew-field"}
                    name={getInputName(TRANSFER_RENEW)}
                    list={transferRenewPrice}
                    component={Dropdown}
                    item={item}
                    type={"transfer"}
                />
            )
        }
        return (<></>)
    }

    return (
        <>
            <div
                className={`of-domain-config__field-wrapper of-domain-config-async${asynValidateRequest && asynValidateRequest.status ? ' ' + asynValidateRequest.status : ''}`}>
                {renderValidatorField()}
                {renderHiddenField()}
                {renderMessages()}
                {renderTransferRenewalField()}
            </div>
            {renderLegalName()}
        </>
    )
}

AsyncValidationField.propTypes = {
    requireButton: PropTypes.bool,
    field: PropTypes.object,
    errorMessages: PropTypes.any,
    getInputName: PropTypes.func,
    asyncValidation: PropTypes.any,
    item: PropTypes.object
}

export default AsyncValidationField